<template>
    <div class="componentRoot">
        <v-card v-if="digitalMediaData">
            <v-container fluid class="pa-0">
                <v-row>
                    <v-col style="position: relative;">
                        <FileInputCardVue :src="digitalMediaData.banner_image_64" :height="'30vh'" @save="patchImage"
                            :align="'top'" />

                        <WaveVue :color="'rgb(var(--v-theme-light))'" :inverted="false" :negative="true"
                            style="position:absolute; z-index: 5; bottom: 8px" />
                    </v-col>
                </v-row>
            </v-container>

            <v-container>
                <v-row>
                    <v-col class="pl-6 pb-0">
                        <v-chip color="primary" class="mr-2" v-for="tag in digitalMediaData.tags">{{ tag.name }}</v-chip>
                        <h1 class="overflow-ellipsis">
                            {{ digitalMediaData.name }}
                        </h1>

                        <v-chip size="small" color="primary" class="my-2" v-if="!profileView">
                            {{ digitalMediaData.is_subscription ? $t("constants.subscription") : $t("constants.one_time") }}
                        </v-chip>
                        <v-btn v-if="subscriptionProfile" variant="text" :loading="portalLoading" @click="openSubscriptionPortal">{{ $t("messages.manageSubscription") }}</v-btn>
                        <p v-if="!profileView &&  digitalMediaData.price != null">{{ digitalMediaData.price }} €{{ digitalMediaData.is_subscription ? ' / Monat' : '' }}</p>
                        <small>
                            {{ $t("basics.durationSimple") }}: ~{{ digitalMediaData.duration }} {{ $t("basics.minutes")
                            }}
                        </small>
                    </v-col>

                    <v-col cols="auto" v-if="!profileView && $checkRouterPermission('PATCH')" class="pa-4 pr-10 pb-0">
                        <v-tooltip :text="$t('basics.new', { type: $t('basics.digitalMedia') })">
                            <template v-slot:activator="{ props }">
                                <div v-bind="props" @click="showEditDigitalMedia($event)" flat><v-icon size="large"
                                        color="primary">mdi-pencil</v-icon></div>
                            </template>
                        </v-tooltip>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="pt-0">
                        <v-textarea variant="solo" rows="1" class="hideDetails" readonly auto-grow flat
                            v-model="digitalMediaData.description"></v-textarea>
                    </v-col>
                </v-row>

                <v-divider class="mb-10 mt-4" />

                <v-row>
                    <v-col class="pb-0 pl-6 pt-0">
                        <h2>{{ $t("basics.digitalMedias") }}</h2>
                    </v-col>
                    <v-spacer />
                    <v-col cols="auto" class="pb-0" v-if="!profileView && $checkFunctionPermission(['digital_media_file_post_detail'])">
                        <v-tooltip :text="$t('basics.new', { type: $t('basics.digitalMediaFile') })">
                            <template v-slot:activator="{ props }">
                                <div v-bind="props" @click="showEditDigitalMediaFile($event)"
                                    class="squareButton hoverable" flat><v-icon color="primary"
                                        size="x-large">mdi-plus</v-icon></div>
                            </template>
                        </v-tooltip>
                    </v-col>
                </v-row>

                <draggable :group="{ name: 'col' }" @end="endDrag" :list="digitalMediaData.files" item-key="id"
                    class="px-0" handle=".rowHandle" style="margin-top: -24px !important">
                    <template #item="{ element: dmf }">
                        <v-col class="px-2" :id="'fileitem_' + dmf.id">
                            <v-container fluid class="lightBorder rounded-lg px-2">
                                <v-row @click="expandDigitalMediaFile($event, dmf)">
                                    <v-col cols="auto" class="pl-6 mt-1 rowHandle"
                                        v-if="!profileView && $checkFunctionPermission(['digital_media_file_order_patch_detail'])">
                                        <v-tooltip :text="$t('basics.drag')">
                                            <template v-slot:activator="{ props }">
                                                <span v-bind="props">
                                                    <v-icon color="primary">mdi-drag-horizontal-variant</v-icon>
                                                </span>
                                            </template>
                                        </v-tooltip>
                                    </v-col>
                                    <v-col cols="auto" class="pl-4">
                                        <h2>{{ dmf.name }}</h2>
                                    </v-col>
                                    <v-spacer />
                                    <v-col cols="auto" style="display: flex" >
                                        <v-tooltip :text="$t('basics.edit', { type: $t('basics.digitalMediaFile') })"
                                        v-if="!profileView && $checkRouterPermission('PATCH')"
                                        >
                                            <template v-slot:activator="{ props }">
                                                <div v-bind="props" class="pr-2 hoverable"
                                                    @click="showEditDigitalMediaFile($event, dmf)" flat>
                                                    <v-icon size="large" color="primary">mdi-pencil</v-icon>
                                                </div>
                                            </template>
                                        </v-tooltip>

                                        <v-tooltip :text="$t('basics.expand')">
                                            <template v-slot:activator="{ props }">
                                                <div v-bind="props" class="pr-2 hoverable"
                                                    @click="expandDigitalMediaFile($event, dmf)" flat>
                                                    <v-icon size="large" color="primary">mdi-chevron-down</v-icon>
                                                </div>
                                            </template>
                                        </v-tooltip>
                                    </v-col>
                                </v-row>

                                <v-expand-transition v-show="dmf.expanded">
                                    <div class="pt-4">
                                        <v-row v-if="dmf.description">
                                            <v-col class="pt-0">
                                                <v-textarea variant="solo" rows="1" class="hideDetails" readonly
                                                    auto-grow flat v-model="dmf.description"></v-textarea>
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12" class="px-6">
                                                <component :is="getFileComponent(dmf)" :file="dmf" />
                                            </v-col>
                                        </v-row>
                                    </div>
                                </v-expand-transition>
                            </v-container>
                        </v-col>
                    </template>
                </draggable>

                <v-row v-if="!digitalMediaData.files || digitalMediaData.files.length == 0" class="mb-8">
                    <v-col class="px-6 mt-4">
                        <i>
                            <p>{{ $t("messages.noItems") }}</p>
                        </i>
                    </v-col>
                </v-row>
            </v-container>
        </v-card>

        <DeletionWarningVue :type="'digitalmedia'" :additionalMessage="$t('messages.confirmDeletionItem', { item: digitalMediaData?.name })
            " :refreshDisplay="deleteDigitalMediaDrawer" @delete="deleteDigitalMedia"
            style="z-index: 1007 !important" />

        <DigitalMediaFileEditVue :refreshDisplay="editDigitalMediaFileDrawer" @close="closeEditDigitalMediaFileEdit"
            :digitalMediaFileData="activeDigitalMediaFile" :digitalMediaData="digitalMediaData"
            :readonly="!$checkFunctionPermission(['digital_media_file_patch_detail'])" />

        <DigitalMediaEditVue :refreshDisplay="editDigitalMediaDrawer" @close="closeEditDigitalMedia"
            :digitalMediaData="digitalMediaData" @delete="showDeleteDigitalMedia"
            :readonly="!$checkRouterPermission('PATCH')" />


        <LoadingScreenVue :loading="loading" />
    </div>
</template>

<script>
import DigitalMediaFileEditVue from '../../../components/products/DigitalMediaFileEdit.vue';
import DigitalMediaEditVue from '../../../components/products/DigitalMediaEdit.vue';

import FileImageVue from "../../../components/products/FileImage.vue";
import FilePdfVue from "../../../components/products/FilePdf.vue";
import FileVideoVue from "../../../components/products/FileVideo.vue";
import FileAudioVue from "../../../components/products/FileAudio.vue";
import FileDownloadVue from "../../../components/products/FileDownload.vue";

export default {
    name: "digitalMediaDetailComponent",

    props: [],

    data() {
        return {
            digitalMediaData: undefined,
            subscriptionProfile: null,
            portalLoading: false,

            loading: false,
            dragging: false,

            activeDigitalMediaFile: undefined,
            editDigitalMediaFileDrawer: 0,

            editDigitalMediaDrawer: 0,

            deleteDigitalMediaDrawer: 0,
        };
    },

    computed: {
        profileView(){
            return String(this.$route.name || "").includes("user")
        },
        isMobile() {
            switch (this.$vuetify.display.name) {
                case "xs":
                    return true;
                case "sm":
                    return true;
                case "md":
                    return false;
                case "lg":
                    return false;
                case "xl":
                    return false;
                case "xxl":
                    return false;
                default:
                    return false;
            }
        },
        searchBarCols() {
            switch (this.$vuetify.display.name) {
                case "xs":
                    return 12;
                case "sm":
                    return 6;
                case "md":
                    return 7;
                case "lg":
                    return 7;
                case "xl":
                    return 7;
                case "xxl":
                    return 6;
                default:
                    return 4;
            }
        },
    },

    components: {
        DigitalMediaFileEditVue,
        DigitalMediaEditVue,
        FileImageVue,
        FilePdfVue,
        FileVideoVue,
        FileAudioVue,
        FileDownloadVue
    },

    methods: {
        async fetchSubscriptionProfile() {
            this.subscriptionProfile = null;
            if (!this.digitalMediaData?.is_subscription) return;
            try {
                const response = await this.axios.get(this.axios.defaults.baseURL + "media/payment/subscriptions/");
                this.subscriptionProfile = response.data.find(profile => profile.digital_media_id === this.digitalMediaData.id && !['canceled', 'incomplete_expired'].includes(profile.status)) || null;
            } catch (error) {
                window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: error }));
            }
        },
        async openSubscriptionPortal() {
            if (!this.subscriptionProfile || this.portalLoading) return;


            this.$router.push("/main/purchases/")
            // this.portalLoading = true;
            // try {
            //     const response = await this.axios.post(this.axios.defaults.baseURL + "media/payment/portal/", {
            //         payment_profile_id: this.subscriptionProfile.id,
            //         return_url: this.$localURL.replace(/\/+$/, "") + this.$route.fullPath,
            //     });
            //     window.location.assign(response.data.portal_url);
            // } catch (error) {
            //     console.log(error)
            //     window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: error }));
            // } finally { this.portalLoading = false; }
        },
        patchImage(image) {
            let formData = new FormData()

            const originalName = image.name || "image.png";
            const dotIndex = originalName.lastIndexOf(".");
            const extension = dotIndex > 0 ? originalName.slice(dotIndex) : "";
            const basename = dotIndex > 0
                ? originalName.slice(0, dotIndex)
                : originalName;

            const filename = extension.length < 100
                ? basename.slice(0, 100 - extension.length) + extension
                : originalName.slice(0, 100);

            formData.append("banner_image", image, filename);

            formData.append("id", this.$route.params.digitalMediaID)

            this.axios
                .patch(
                    this.axios.defaults.baseURL + "media/digitalmedias/patch/" + this.$route.params.digitalMediaID,
                    formData
                )
                .then((resp) => {
                    window.dispatchEvent(new CustomEvent("showSaveDisplay"))
                })
                .catch((e) => {
                    console.log(e);
                    window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
                });
        },
        endDrag(val) {
            this.loading = true

            let id = val.item.id.split("_")
            id = id[id.length - 1]

            this.axios.patch(this.axios.defaults.baseURL + "media/digitalmediafiles/order/patch/", {
                old: val.oldIndex,
                new: val.newIndex,
                id: id
            })
                .then((resp) => {
                    this.loading = false
                    this.fetchData()

                }).catch((e) => {
                    this.loading = false
                    console.log(e);
                    window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
                });
        },
        expandDigitalMediaFile(event, dmf) {
            event.stopPropagation()
            event.preventDefault()

            if (!dmf.expanded) {
                dmf.expanded = true
            }
            else {
                dmf.expanded = false
            }
        },
        showDeleteDigitalMedia(digitalmedia) {
            this.activeDigitalMedia = digitalmedia;
            this.deleteDigitalMediaDrawer++;
        },
        deleteDigitalMedia() {
            this.loading = true;
            this.axios
                .patch(
                    this.axios.defaults.baseURL +
                    "media/digitalmedias/delete/" +
                    this.digitalMediaData.id,
                    {
                        deleted: true,
                    }
                )
                .then(() => {
                    this.loading = false;
                    this.deleteDigitalMediaDrawer = 0;
                    this.editDigitalMediaDrawer = 0;
                    this.fetchData();
                })
                .catch((e) => {
                    this.loading = false;
                    console.log(e);
                    window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
                });
        },
        downloadFile() {
            const link = document.createElement("a");
            link.href = this.file.file_64;
            link.download = this.file.name || "download";
            link.click();
        },
        getMimeType(dataUrl) {
            const match = dataUrl?.match(/^data:(.*?);base64,/);
            return match ? match[1] : "";
        },
        getFileComponent(file) {
            const mime = this.getMimeType(file.file_64);

            if (mime.startsWith("image/")) return "FileImageVue";
            if (mime === "application/pdf") return "FilePdfVue";
            if (mime.startsWith("video/")) return "FileVideoVue";
            if (mime.startsWith("audio/")) return "FileAudioVue";

            return "FileDownloadVue"; // fallback (or make a FileDownload.vue if preferred)
        },
        showDeleteDigitalMedia(digitalmedia) {
            this.activeDigitalMedia = digitalmedia;
            this.deleteDigitalMediaDrawer++;
        },
        showEditDigitalMedia(event, digitalmedia) {
            event.preventDefault();
            event.stopPropagation();

            this.activeDigitalMedia = digitalmedia;
            this.editDigitalMediaDrawer++;
        },
        closeEditDigitalMedia(data) {
            this.editDigitalMediaDrawer = 0;

            if (data) {
                this.fetchData()
            }
        },
        closeEditDigitalMediaFileEdit(data) {
            this.activeDigitalMediaFile = undefined;
            this.editDigitalMediaFileDrawer = 0;

            if (data) {
                this.fetchData()
            }
        },
        showEditDigitalMediaFile(event, digitalMediaFile) {
            event.preventDefault();
            event.stopPropagation();

            this.activeDigitalMediaFile = digitalMediaFile;
            this.editDigitalMediaFileDrawer++;
        },
        fetchData() {
        this.loading = true
                this.axios.get(this.axios.defaults.baseURL + "media/digitalmedias/" + this.$route.params.digitalMediaID)
                    .then((resp) => {
                        this.digitalMediaData = resp.data
                        this.fetchSubscriptionProfile()
                        this.loading = false

                        this.$store.dispatch("setActiveElem", { digitalMediaID: resp.data.name });
                    })

                    .catch((e) => {
                        this.loading = false
                        console.log(e);
                        window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
                    });

        },
    },
    mounted() {

        this.fetchData()
    },
};
</script>
